"use client";

import { agentSignupAction } from "@/app/auth-actions";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { type AgentProfileData, AgentSignupForm } from "./agent-signup-form";

export default function SignupClient() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (data: AgentProfileData) => {
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await agentSignupAction(data);

      if (!result) {
        setError("An unexpected error occurred. Please try again.");
        setIsSubmitting(false);
        return;
      }

      if ("error" in result && result.error) {
        setError(result.error);
        setIsSubmitting(false);
        return;
      }

      if ("success" in result && result.success) {
        const successMessage = "message" in result ? result.message : "Account created successfully!";
        setSuccess((successMessage as string) || "Account created successfully!");
        setIsSubmitting(false);
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-gray-950 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link href="/" className="inline-block mb-6">
            <Image
              src="/v2/kolz.svg"
              alt="kolz.chat logo"
              height={47}
              width={151}
              className="h-10 w-36 sm:h-14 sm:w-48 mx-auto"
            />
          </Link>
          <p className="text-xl text-white font-semibold">Agent Setup</p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="max-w-3xl mx-auto p-8 bg-gray-900 rounded-lg border border-[#43BEA9]">
            <div className="text-center">
              <div className="mb-4">
                <svg className="w-16 h-16 mx-auto text-[#43BEA9]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <title>Success</title>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">All Set Up! 🎉</h2>
              <p className="text-gray-400 text-lg mb-4">Please check your email to verify your account.</p>
              <p className="text-sm text-gray-500">
                We've sent a verification link to your email address. Click the link to activate your account and get
                started.
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="max-w-3xl mx-auto mb-6 p-4 bg-red-900/20 border border-red-500 rounded-lg">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Form - Hidden on Success */}
        {!success && (
          <>
            <AgentSignupForm onSubmit={handleSubmit} isSubmitting={isSubmitting} />

            {/* Footer */}
            <div className="text-center mt-8">
              <p className="text-sm text-gray-400">
                Already have an account?{" "}
                <Link
                  className="text-[#43BEA9] font-medium underline hover:text-[#3AAD98] transition-colors"
                  href="/sign-in"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
